import React from 'react'
import Dotdotdot from 'react-dotdotdot'
import { Grid, Stack } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'

import { WarningRound as WarningRoundIcon } from '../../../icons'
import { ClassesType, PrimitiveTableColumn } from '../../../types'
import { getHighlightedText } from '../../../utils'
import {
  getColumnKey,
  getColumnStyles,
} from '../../../utils/primitiveTableUtils'
import ControlButtonGroup, {
  ControlButtonGroupName,
} from '../../buttons/ControlButtonGroup'
import PuiCheckbox from '../../inputs/PuiCheckbox'
import TextInteractive from '../../TextInteractive'

const useStyles = makeStyles(
  theme => ({
    listItem: {
      paddingBottom: theme.spacing(1),
    },
    container: {
      minHeight: (props: UseStylesProps) => props.minHeight || 56,
      height: '100%',
      userSelect: 'none',
      cursor: 'pointer',
      borderRadius: 2,
      border: theme.constants.tableBorder,
      borderColor: (props: UseStylesProps) => props.rowColoring,
      backgroundColor: theme.colors.tableBackground,
      [theme.breakpoints.up('md')]: {
        padding: `calc(${theme.spacing(1)} - 2px) ${theme.spacing(2)}`,
      },
    },
    containerNoColoring: {},
    listShadow: {
      '&:hover': {
        boxShadow: theme.constants.listItemShadow,
      },
    },
    selectedRow: {
      border: theme.constants.tableBorderSelected,
    },
    disabledRow: {
      backgroundColor: theme.colors.tabDisabled,
    },
    text: {
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
    },
    textSelected: {
      color: theme.colors.tabSelected,
    },
    textDisabled: {
      color: theme.colors.tabLabelDisabled,
    },
    mainInfoContainer: {
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(2),
        justifyContent: 'flex-start',
      },
    },
    sideInfoContainer: {
      position: 'relative',
      lineHeight: '2.4rem',
    },
    expandedColumn: {
      transition: theme.transitions.create(['max-width', 'flex-basis'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    collapsedColumn: {
      overflow: 'hidden',
      transition: theme.transitions.create(['max-width', 'flex-basis'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      '&&': {
        padding: 0,
      },
    },
    expandedMainColumn: {
      minWidth: 120,
      transition: theme.transitions.create(['max-width', 'flex-basis'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen / 2,
        delay: 100,
      }),
    },
    noOverflow: {
      overflow: 'hidden',
      height: '100%',
    },
    controlButtonGroup: {
      minWidth: 75,
    },
    warnIcon: {
      marginRight: theme.spacing(0.5),
      fontSize: '2.4rem',
      color: theme.colors.important,
    },
    checkboxRoot: {
      marginRight: 0,
      marginLeft: theme.spacing(-1),
    },
  }),
  { name: 'PrimitiveTableRow' },
)

const getDisplayedName = (column: PrimitiveTableColumn, item: any) =>
  column.prop
    ? typeof column.prop === 'function'
      ? column.prop(item)
      : item[column.prop]
    : ''

export interface PrimitiveTableRowData {
  classes?: ClassesType<typeof useStyles>
  expandable: boolean
  getDisabled: (item: any) => boolean
  getItemSize: number | (() => number)
  getRowColoring: (item: any) => string
  getUniqueId: (item: any) => string
  isItemSelected: (item: any) => boolean
  isMobile: boolean
  isSelectMode: boolean
  mainColumnWidth: number
  mainColumns: PrimitiveTableColumn[]
  onClick?: (item: any) => void
  onItemDelete?: (id: string) => void
  onItemDuplicate?: (id: string) => void
  onSelectionChange: (id: string, selection: boolean) => void
  otherColumns: PrimitiveTableColumn[]
  rowFocused: string
}

export interface PrimitiveTableRowProps {
  readonly clamp?: number
  readonly data: PrimitiveTableRowData
  readonly item?: any
  readonly noOverflow?: boolean
  readonly searchHighlights?: string[]
}

interface UseStylesProps {
  classes?: ClassesType<typeof useStyles>
  mainColumnWidth: number
  minHeight: number
  rowColoring: string
}

const PrimitiveTableRow = ({
  item: itemProp,
  data: {
    isMobile,
    onClick,
    rowFocused,
    mainColumns,
    otherColumns,
    expandable,
    getRowColoring,
    getDisabled,
    mainColumnWidth,
    onItemDelete,
    getItemSize,
    onItemDuplicate,
    classes: classesProp,
    getUniqueId,
    isSelectMode,
    isItemSelected,
    onSelectionChange,
  },
  clamp = 2,
  noOverflow = true,
  searchHighlights,
}: PrimitiveTableRowProps) => {
  const item = itemProp || {}

  const isEmptyRow = R.isEmpty(item)
  const minHeight =
    typeof getItemSize === 'function' ? getItemSize() : getItemSize
  const rowColoring = getRowColoring && getRowColoring(item)
  const useStylesProps: UseStylesProps = {
    minHeight,
    rowColoring,
    mainColumnWidth,
    classes: classesProp,
  }
  const classes = useStyles(useStylesProps)

  const isFocusedRow = !R.isNil(rowFocused) && rowFocused === getUniqueId(item)
  const hasFocusedRow = !R.isNil(rowFocused)
  const isDisabledRow = getDisabled && getDisabled(item)

  const rows = otherColumns.map(
    (
      {
        width,
        prop,
        label,
        sublabel,
        color,
        component,
        hasWarning,
        noTypography,
        hidden,
        columnWrap,
      },
      index,
    ) => {
      const value = prop
        ? typeof prop === 'function'
          ? prop(item)
          : item[prop]
        : ''
      return !hidden ? (
        <Grid
          container
          item
          alignItems="center"
          className={classes.sideInfoContainer}
          key={getColumnKey({ label, sublabel }, index)}
          minWidth={20}
          pr={isMobile ? undefined : 1}
          style={getColumnStyles(width)}
          wrap={columnWrap}
        >
          {component ? (
            component(item)
          ) : (
            <>
              {hasWarning && hasWarning(item) && !hasFocusedRow && (
                <WarningRoundIcon className={classes.warnIcon} />
              )}
              <Dotdotdot clamp={clamp}>
                {noTypography ? (
                  value
                ) : (
                  <TextInteractive
                    className={classNames(classes.text, {
                      [classes.textDisabled]: isDisabledRow,
                    })}
                    isLoading={isEmptyRow}
                    style={{ color: color ? color(item) : undefined }}
                  >
                    {value}
                  </TextInteractive>
                )}
              </Dotdotdot>
            </>
          )}
        </Grid>
      ) : null
    },
  )

  const handleDelete = (event: any) => {
    event.stopPropagation()
    if (onItemDelete) {
      onItemDelete(item.id)
    }
  }

  const handleDuplicate = (event: any) => {
    event.stopPropagation()
    if (onItemDuplicate) {
      onItemDuplicate(item.id)
    }
  }

  const onCheckboxChanged = () => {
    const wasSelected = isItemSelected(item)
    onSelectionChange(item, !wasSelected)
  }

  return (
    <Grid
      container
      alignItems={isMobile ? 'flex-start' : 'center'}
      className={classNames(classes.container, {
        [classes.listShadow]: !isFocusedRow,
        [classes.selectedRow]: isFocusedRow,
        [classes.disabledRow]: isDisabledRow,
        [classes.containerNoColoring]: !rowColoring,
      })}
      direction={isMobile ? 'column' : 'row'}
      justifyContent="center"
      onClick={onClick && !isEmptyRow ? () => onClick(item) : undefined}
    >
      <Stack
        alignItems="center"
        className={classNames({
          [classes.noOverflow]: noOverflow,
        })}
        direction="row"
      >
        {!hasFocusedRow && isSelectMode && (
          <PuiCheckbox
            checked={isItemSelected(item)}
            classes={{
              labelRoot: classes.checkboxRoot,
            }}
            onChange={onCheckboxChanged}
            onClick={event => event.stopPropagation()}
          />
        )}
        <Grid
          container
          item
          alignItems="center"
          className={classNames(classes.mainInfoContainer, {
            [classes.expandedMainColumn]: !hasFocusedRow,
          })}
          style={isMobile ? undefined : getColumnStyles(mainColumnWidth)}
          wrap="nowrap"
        >
          {mainColumns.map((column, index) => {
            const displayedValue = getDisplayedName(column, item)
            return (
              <Grid
                key={getColumnKey(column, index)}
                pr={1}
                style={getColumnStyles(
                  (column.width * 12) / (hasFocusedRow ? 1 : mainColumnWidth),
                )}
              >
                {column.component ? (
                  column.component(item)
                ) : column.noTypography ? (
                  displayedValue
                ) : (
                  <Dotdotdot clamp={clamp}>
                    <TextInteractive
                      className={classNames(classes.text, {
                        [classes.textSelected]: isFocusedRow,
                        [classes.textDisabled]: isDisabledRow && !isFocusedRow,
                      })}
                      isLoading={isEmptyRow}
                      {...(column.highlight
                        ? {
                          highlight: getHighlightedText(
                            displayedValue,
                            searchHighlights,
                          ),
                        }
                        : {})}
                    >
                      {displayedValue}
                    </TextInteractive>
                  </Dotdotdot>
                )}
              </Grid>
            )
          })}
        </Grid>
        {expandable ? (
          <Stack
            alignItems="center"
            className={classNames({
              [classes.collapsedColumn]: !isMobile && hasFocusedRow,
              [classes.expandedColumn]: !isMobile && !hasFocusedRow,
            })}
            direction={isMobile ? 'column-reverse' : 'row'}
            justifyContent={isMobile ? 'flex-end' : 'flex-start'}
            style={
              isMobile
                ? undefined
                : getColumnStyles(hasFocusedRow ? 0 : 12 - mainColumnWidth)
            }
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {rows}
            {(onItemDelete || onItemDuplicate) && (
              <Grid
                container
                item
                alignItems="center"
                className={classNames(
                  classes.controlButtonGroup,
                  classes.sideInfoContainer,
                )}
                md={1}
                wrap="nowrap"
              >
                <ControlButtonGroup
                  buttons={[
                    onItemDelete && {
                      name: ControlButtonGroupName.DELETE,
                      onClick: handleDelete,
                    },
                    onItemDuplicate && {
                      name: ControlButtonGroupName.DUPLICATE,
                      onClick: handleDuplicate,
                    },
                  ]}
                />
              </Grid>
            )}
          </Stack>
        ) : (
          rows
        )}
      </Stack>
    </Grid>
  )
}

export default PrimitiveTableRow
